/**
 * The trial pages' only way to the backend.
 *
 * Deliberately not `src/app/http-common.js`: that client attaches a Frontegg
 * bearer token and redirects to login on 401. A trial subscriber has no account,
 * so a 401 interceptor would bounce them out of their own feed. These endpoints
 * are public and token-scoped, the same shape the landing pages call.
 */

import axios from 'axios';

const base = () => process.env.REACT_APP_BASE_URL || '/api';

/**
 * Normalise an axios failure into the two things a page can act on.
 *
 * `code` matters more than the message: the expired-link screen keys off
 * `TRIAL_LINK_EXPIRED` rather than matching prose, so copy can change without
 * silently turning a recovery flow back into a dead end.
 */
function toError(err) {
  const payload = err?.response?.data || {};
  const e = new Error(payload.error || 'Something went wrong.');
  e.code = payload.code || 'TRIAL_ERROR';
  e.status = err?.response?.status || 0;
  return e;
}

async function get(path, config) {
  try {
    const { data } = await axios.get(`${base()}${path}`, config);
    return data;
  } catch (err) {
    throw toError(err);
  }
}

async function post(path, body, config) {
  try {
    const { data } = await axios.post(`${base()}${path}`, body, config);
    return data;
  } catch (err) {
    throw toError(err);
  }
}

const feed = (token) => `/public/trial/feed/${encodeURIComponent(token)}`;

export const trialApi = {
  getFeed: (token, config) => get(feed(token), config),
  getStory: (token, storyId, config) => get(`${feed(token)}/story/${encodeURIComponent(storyId)}`, config),
  /** `value` is 'relevant', 'not_relevant', or null to clear. */
  setFeedback: (token, storyId, value) => post(`${feed(token)}/story/${encodeURIComponent(storyId)}/feedback`, { value }),
  getPreferences: (token, config) => get(`${feed(token)}/preferences`, config),
  requestDraft: (token, storyId) => post(`${feed(token)}/story/${encodeURIComponent(storyId)}/draft`, {}),
  getDraft: (token, reportId, config) => get(`${feed(token)}/draft/${encodeURIComponent(reportId)}`, config),
  savePreferences: (token, topics, freeText = '') => post(`${feed(token)}/preferences`, { topics, freeText }),
};

export default trialApi;
